(() => {
  "use strict";

  const SLOT_COUNT = 5;
  const EXPECTED_FLAVOUR_COUNT = 8;
  const DRAFT_VERSION = 1;
  const SOUND_STORAGE_KEY = "flss-gift-sound-enabled";
  const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
  const GUIDE_STEPS = ["flavours", "message", "delivery", "review"];
  const EXAMPLE_RECIPIENT = "Alex";
  const EXAMPLE_MESSAGE = "Hope this adds a little lekker to your day. Enjoy every flavour!";
  const STUDIO_EVENTS = new Set(["builder_start", "step_complete", "step_view", "review", "import_preview", "import_error", "import_applied", "cart_added", "cart_error"]);
  const STUDIO_CATEGORIES = new Set([...GUIDE_STEPS, "file_or_schema", "row_validation", "cart_check", "capacity", "draft_pending", "draft_storage", "sold_out", "incomplete_box", "delivery_date", "message_fit", "shopify_rejection", "cart_uncertain"]);

  const escapeHtml = (value) => String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
  const clamp = (value, min, max) => Math.min(max, Math.max(min, Number(value) || min));
  const normalizeText = (value, maxLength) => String(value ?? "").replace(/\s+/g, " ").trim().slice(0, maxLength);
  const normalizeMessage = (value, maxLength) => String(value ?? "")
    .replace(/\s+/g, " ")
    .trimStart()
    .slice(0, maxLength);
  const normalizeDeliveryDate = (value) => {
    const normalized = String(value ?? "").trim();
    const match = normalized.match(ISO_DATE_PATTERN);
    if (!match) return "";
    const [, year, month, day] = match;
    const parsed = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
    return parsed.getUTCFullYear() === Number(year)
      && parsed.getUTCMonth() === Number(month) - 1
      && parsed.getUTCDate() === Number(day)
      ? normalized
      : "";
  };
  const todayIso = () => {
    const parts = new Intl.DateTimeFormat("en-ZA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      timeZone: "Africa/Johannesburg"
    }).formatToParts(new Date());
    const calendar = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return `${calendar.year}-${calendar.month}-${calendar.day}`;
  };
  const formatDeliveryDate = (value) => {
    const normalized = normalizeDeliveryDate(value);
    if (!normalized) return "";
    const [year, month, day] = normalized.split("-").map(Number);
    return new Intl.DateTimeFormat(document.documentElement.lang || "en-ZA", {
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: "UTC"
    }).format(new Date(Date.UTC(year, month - 1, day)));
  };
  const uid = () => globalThis.crypto?.randomUUID?.()
    || `gift-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

  function cartErrorMessage(payload, status) {
    const maxLength = 500;
    const sliceMessage = (value, limit) => value.slice(0, limit).replace(/[\ud800-\udbff]$/, "");
    for (const detail of [payload?.description, payload?.errors, payload?.message]) {
      const messages = [];
      let remainingNodes = 30;
      const collect = (value, depth = 0) => {
        if (depth > 4 || remainingNodes-- <= 0 || messages.length >= 3) return;
        if (typeof value === "string") {
          const message = sliceMessage(value, 2000)
            .replace(/[\u0000-\u001f\u007f-\u009f]/g, " ")
            .replace(/\s+/g, " ")
            .trim();
          // Do not show an upstream HTML error page as customer guidance.
          if (message && !/<(?:!doctype|\/?[a-z][^>]*>)/i.test(message) && !messages.includes(message)) {
            messages.push(message);
          }
        } else if (value && typeof value === "object") {
          for (const child of Object.values(value)) {
            collect(child, depth + 1);
            if (remainingNodes <= 0 || messages.length >= 3) break;
          }
        }
      };
      collect(detail);
      if (messages.length) {
        const message = messages.join(" ");
        return message.length > maxLength ? `${sliceMessage(message, maxLength - 1).trimEnd()}…` : message;
      }
    }
    const statusLabel = Number.isInteger(status) && status >= 400 && status <= 599
      ? ` (Shopify error ${status})`
      : "";
    return `The gift boxes could not be added to the cart${statusLabel}. Please try again.`;
  }

  function createBox(messageLimit, source = {}) {
    const recipientLabel = normalizeText(source?.recipientLabel || source?.brief?.recipient, 80);
    return {
      id: normalizeText(source?.id, 120) || uid(),
      slots: Array.from({ length: SLOT_COUNT }, (_, index) => String(source?.slots?.[index] || "")),
      message: normalizeMessage(source?.message, messageLimit),
      recipientLabel,
      brief: {
        recipient: recipientLabel,
        occasion: normalizeText(source?.brief?.occasion, 80),
        sender: normalizeText(source?.brief?.sender, 80),
        tone: normalizeText(source?.brief?.tone, 40) || "warm",
        language: normalizeText(source?.brief?.language, 40) || "English",
        context: normalizeText(source?.brief?.context, 320)
      }
    };
  }

  class GiftBoxBuilder {
    constructor(root) {
      this.root = root;
      this.failed = false;
      this.suggestions = [];
      this.adding = false;
      this.generating = false;
      this.messageFitByBoxId = new Map();
      this.randomMixByBoxId = new Map();
      this.soundEnabled = this.loadSoundPreference();
      this.audioContext = null;
      this.celebrationTimer = null;
      this.flavourPickerReturnKey = "";
      this.reducedMotion = globalThis.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches === true;
      this.guideStep = "flavours";
      this.guidePreviewOpen = false;
      this.guideFlavourId = "";
      this.mobileQuery = globalThis.matchMedia?.("(max-width: 749px)");

      try {
        this.config = JSON.parse(root.querySelector("[data-fl-gift-config]")?.textContent || "{}");
        this.config.maxBoxes = clamp(this.config.maxBoxes || 20, 1, 25);
        this.config.messageLimit = clamp(this.config.messageLimit || 180, 100, 180);
        this.config.product = {
          ...(this.config.product || {}),
          variantId: String(this.config.product?.variantId || ""),
          available: this.config.product?.available === true
        };
        this.config.flavours = (Array.isArray(this.config.flavours) ? this.config.flavours : [])
          .filter((entry) => entry?.variantId)
          .map((entry) => ({
            ...entry,
            variantId: String(entry.variantId),
            available: entry.available === true
          }));

        if (!this.config.product.variantId) {
          throw new Error("The gift box product is not configured correctly.");
        }
        if (this.config.flavours.length !== EXPECTED_FLAVOUR_COUNT) {
          throw new Error("One or more 200ml flavours could not be loaded. Please refresh or contact Flippen Lekka.");
        }
      } catch (error) {
        this.failed = true;
        root.innerHTML = `<div class="fl-gift-builder__error" role="alert">${escapeHtml(error.message || "The gift box builder could not be loaded.")}</div>`;
        return;
      }

      this.flavourById = new Map(
        this.config.flavours.map((entry) => [entry.variantId, entry])
      );
      this.storageKey = `flss-gift-box-draft:${this.config.product?.id || this.config.product.variantId || "gift"}`;
      const savedDraft = this.loadDraft();
      // Ordinary Studio drafts belong to this tab and must match the live cart
      // before they are shown. Uncertain submissions retain their recovery lock.
      this.studioDraftToRestore = this.config.studioEnabled && !savedDraft?.pendingCart ? savedDraft : null;
      this.state = (this.config.studioEnabled ? savedDraft?.pendingCart && savedDraft : savedDraft) || this.emptyDraft();
    }

    emptyDraft() {
      return {
        version: DRAFT_VERSION,
        groupId: uid(),
        activeIndex: 0,
        selectedSlot: 0,
        preferredDeliveryDate: "",
        boxes: [createBox(this.config.messageLimit)]
      };
    }

    start() {
      if (this.failed) return;
      if (this.config.studioEnabled) this.studioAudience = new URLSearchParams(window.location.search).get("gift_mode") === "team" ? "team" : "personal";
      this.prepareStudioPrivacy();
      this.bind();
      this.syncGuideViewport();
      this.mobileQuery?.addEventListener?.("change", () => {
        this.syncGuideViewport();
        this.render();
      });
      this.render();
      if (this.config.studioEnabled) globalThis.FlGiftStudio?.attach(this, createBox);
      if (!this.config.product.available) {
        this.notice("This gift box is currently sold out.", "error");
      }
    }

    loadDraft() {
      try {
        const recovery = JSON.parse(localStorage.getItem(this.storageKey) || "null");
        const parsed = this.config.studioEnabled && !recovery?.pendingCart
          ? JSON.parse(sessionStorage.getItem(this.storageKey) || "null") : recovery;
        if (!parsed || parsed.version !== DRAFT_VERSION || !Array.isArray(parsed.boxes) || !parsed.boxes.length) {
          return null;
        }
        const boxes = parsed.boxes
          .slice(0, this.config.maxBoxes)
          .map((box) => createBox(this.config.messageLimit, box));
        return {
          version: DRAFT_VERSION,
          groupId: normalizeText(parsed.groupId, 120) || uid(),
          ...(parsed.pendingCart === true ? { pendingCart: true } : {}),
          ...(typeof parsed.cartToken === "string" && parsed.cartToken ? {
            cartToken: parsed.cartToken, cartHadItems: parsed.cartHadItems === true
          } : {}),
          activeIndex: clamp(parsed.activeIndex || 0, 0, boxes.length - 1),
          selectedSlot: clamp(parsed.selectedSlot || 0, 0, SLOT_COUNT - 1),
          preferredDeliveryDate: normalizeDeliveryDate(parsed.preferredDeliveryDate) >= todayIso()
            ? normalizeDeliveryDate(parsed.preferredDeliveryDate)
            : "",
          boxes
        };
      } catch {
        return null;
      }
    }

    saveDraft() {
      if (this.config.studioEnabled) {
        // Input made while the initial cart request is in flight takes priority.
        this.studioDraftToRestore = null;
        try { sessionStorage.setItem(this.storageKey, JSON.stringify(this.state)); } catch { /* in-memory editing still works */ }
        try {
          if (this.state.pendingCart) localStorage.setItem(this.storageKey, JSON.stringify(this.state));
          else localStorage.removeItem(this.storageKey);
        } catch { /* storage can be blocked */ }
        return;
      }
      try {
        localStorage.setItem(this.storageKey, JSON.stringify(this.state));
      } catch {
        // Storage can be blocked in private browsing; checkout must still work.
      }
    }

    discardSavedDraft() {
      this.studioDraftToRestore = null;
      try { sessionStorage.removeItem(this.storageKey); } catch { /* storage can be blocked */ }
      try { localStorage.removeItem(this.storageKey); } catch { /* storage can be blocked */ }
    }

    resetStudioDraft({ cartHadItems = this.state.cartHadItems } = {}) {
      const cartToken = this.state.cartToken;
      this.state = { ...this.emptyDraft(), ...(cartToken ? { cartToken, cartHadItems } : {}) };
      this.guideStep = "flavours";
      this.suggestions = [];
      this.messageFitByBoxId?.clear();
      this.randomMixByBoxId?.clear();
      this.discardSavedDraft();
    }

    reconcileStudioDraft(cart) {
      const token = typeof cart.token === "string" ? cart.token : "";
      const hasItems = cart.items.length > 0;
      const matchesCart = draft => token && draft.cartToken === token && !(draft.cartHadItems && !hasItems);
      if (this.studioDraftToRestore) {
        if (matchesCart(this.studioDraftToRestore)) this.state = this.studioDraftToRestore;
        this.studioDraftToRestore = null;
      }
      if (!this.state.pendingCart && token && this.state.cartToken && !matchesCart(this.state)) {
        this.resetStudioDraft();
        this.notice("Your new cart starts with an empty gift box. Choose your five flavours.");
      }
      // Preserve an uncertain submission even if the cart changed. Explicit
      // recovery is still required before it can ever be sent again.
      if (!this.state.pendingCart) {
        this.state.cartToken = token;
        this.state.cartHadItems = hasItems;
        this.saveDraft();
      }
    }

    activeBox() {
      return this.state.boxes[this.state.activeIndex];
    }

    flavour(id) {
      return this.flavourById.get(String(id)) || null;
    }

    boxReady(box) {
      return box.slots.length === SLOT_COUNT
        && box.slots.every((id) => this.flavour(id)?.available === true);
    }

    boxMessageFits(box) {
      return (!box?.message && !box?.recipientLabel) || this.messageFitByBoxId.get(box.id) !== false;
    }

    allPreviewMessagesFit() {
      return this.state.boxes.every((box) => this.boxMessageFits(box));
    }

    invalidateMessageFit(box) {
      if (box?.id) this.messageFitByBoxId.delete(box.id);
    }

    syncAddButtonState() {
      const add = this.root.querySelector("[data-add-gift-boxes]");
      if (!add) return;
      add.disabled = this.adding
        || !this.config.product.available
        || !this.state.boxes.every((entry) => this.boxReady(entry))
        || !this.allPreviewMessagesFit();
    }

    boxHasContent(box) {
      const brief = box?.brief || {};
      return Boolean(
        box?.slots?.some(Boolean)
        || box?.message
        || brief.recipient
        || brief.occasion
        || brief.sender
        || brief.context
        || (brief.tone && brief.tone !== "warm")
        || (brief.language && brief.language !== "English")
      );
    }

    flavourName(id) {
      return this.flavour(id)?.name || "Not selected";
    }

    flavourLabel(id) {
      const flavour = this.flavour(id);
      return flavour?.displayName || flavour?.name || "Not selected";
    }

    setPreviewView(view) {
      if (!["front", "back"].includes(view) || !this.config.studioEnabled) return;
      const panel = this.root.querySelector(`[data-preview-panel="${view}"]`);
      if (!panel) return;
      this.root.querySelectorAll("[data-preview-panel]").forEach(node => { node.hidden = node !== panel; });
      this.root.querySelectorAll("[data-preview-view]").forEach(button => {
        button.setAttribute("aria-pressed", String(button.dataset.previewView === view));
      });
      this.renderPreviewMessage();
    }

    flavourImage(id) {
      return this.flavour(id)?.image || "";
    }

    loadSoundPreference() {
      try {
        return globalThis.localStorage?.getItem(SOUND_STORAGE_KEY) === "true";
      } catch {
        return false;
      }
    }

    saveSoundPreference() {
      try {
        globalThis.localStorage?.setItem(SOUND_STORAGE_KEY, String(this.soundEnabled));
      } catch {
        // Sound still works for this visit when browser storage is unavailable.
      }
    }

    syncSoundControl() {
      const toggle = this.root.querySelector("[data-gift-sound-toggle]");
      if (!toggle) return;
      toggle.setAttribute("aria-pressed", String(this.soundEnabled));
      toggle.setAttribute("aria-label", `Turn selection sounds ${this.soundEnabled ? "off" : "on"}`);
      toggle.textContent = this.soundEnabled ? "Sound on" : "Sound off";
    }

    toggleSound() {
      this.soundEnabled = !this.soundEnabled;
      this.saveSoundPreference();
      if (this.soundEnabled) {
        this.playSound("toggle");
      } else if (this.audioContext) {
        void this.audioContext.close?.();
        this.audioContext = null;
      }
      this.syncSoundControl();
      this.notice(`Selection sounds ${this.soundEnabled ? "on" : "off"}.`, "info");
    }

    playSound(kind = "select") {
      if (!this.soundEnabled) return;
      const AudioContext = globalThis.AudioContext || globalThis.webkitAudioContext;
      if (!AudioContext) return;
      try {
        const context = this.audioContext || new AudioContext();
        this.audioContext = context;
        if (context.state === "suspended") void context.resume?.();
        const frequencies = kind === "complete"
          ? [523.25, 659.25, 783.99]
          : kind === "remove" ? [392] : kind === "move" ? [440, 493.88] : [523.25];
        const start = context.currentTime + 0.01;
        frequencies.forEach((frequency, index) => {
          const offset = index * 0.065;
          const oscillator = context.createOscillator();
          const gain = context.createGain();
          oscillator.type = "sine";
          oscillator.frequency.setValueAtTime(frequency, start + offset);
          gain.gain.setValueAtTime(0.0001, start + offset);
          gain.gain.exponentialRampToValueAtTime(0.035, start + offset + 0.012);
          gain.gain.exponentialRampToValueAtTime(0.0001, start + offset + 0.11);
          oscillator.connect(gain);
          gain.connect(context.destination);
          oscillator.start(start + offset);
          oscillator.stop(start + offset + 0.12);
        });
      } catch {
        this.audioContext = null;
      }
    }

    nextOpenSlot(slots, currentIndex) {
      for (let offset = 1; offset <= SLOT_COUNT; offset += 1) {
        const index = (currentIndex + offset) % SLOT_COUNT;
        if (!slots[index]) return index;
      }
      return currentIndex;
    }

    syncGuideViewport() {
      const mobile = (this.mobileQuery?.matches === true || this.config.studioEnabled === true) && Boolean(this.root.querySelector("[data-guide-nav]"));
      const wasMobile = this.root.classList.contains("is-guided-mobile");
      this.root.classList.toggle("is-guided-mobile", mobile);
      if (mobile !== wasMobile) {
        const assistant = this.root.querySelector("[data-message-assistant]");
        if (assistant) assistant.open = !mobile;
        if (mobile) this.closeFlavourPicker();
        else this.guidePreviewOpen = false;
      }
      this.renderGuide();
    }

    focusGuideStart() {
      if (this.config.studioEnabled) {
        const controls = this.root.querySelector(".fl-gift-builder__configuration");
        if (controls) controls.scrollTop = 0;
      }
      const nav = this.root.querySelector("[data-guide-nav]");
      nav?.focus?.({ preventScroll: true });
      nav?.scrollIntoView?.({ behavior: "instant", block: "start" });
    }

    focusGuideNotice() {
      if (!this.root.classList?.contains("is-guided-mobile")) return;
      const node = this.root.querySelector("[data-guide-notice]");
      if (!node?.textContent) return;
      node.focus?.({ preventScroll: true });
      node.scrollIntoView?.({ behavior: "instant", block: "start" });
    }

    setGuideStep(step, { allowIncomplete = false, focus = true } = {}) {
      if (!GUIDE_STEPS.includes(step) || this.adding) return false;
      if (!allowIncomplete && step !== "flavours" && !this.boxReady(this.activeBox())) {
        this.guideStep = "flavours";
        this.guidePreviewOpen = false;
        this.renderGuide();
        this.notice("Choose all five available bottles before continuing.", "error");
        if (focus) this.focusGuideStart();
        return false;
      }
      if (!allowIncomplete && ["delivery", "review"].includes(step) && !this.fitPreviewMessage()) {
        this.guideStep = "message";
        this.guidePreviewOpen = false;
        this.renderGuide();
        this.notice("Shorten this message so it fits the printed label.", "error");
        if (focus) this.focusGuideStart();
        return false;
      }
      if (GUIDE_STEPS.indexOf(step) === GUIDE_STEPS.indexOf(this.guideStep) + 1) this.studioEvent("step_complete", { category: this.guideStep });
      this.guideStep = step;
      if (step === "message") this.setPreviewView("back");
      else if (step === "flavours") this.setPreviewView("front");
      this.guidePreviewOpen = false;
      this.notice("");
      this.render();
      if (focus) this.focusGuideStart();
      return true;
    }

    selectGuideSlot(index) {
      if (this.adding) return;
      this.state.selectedSlot = clamp(index, 0, SLOT_COUNT - 1);
      this.guideFlavourId = this.activeBox().slots[this.state.selectedSlot] || this.guideFlavourId;
      this.saveDraft();
      this.renderGuide();
    }

    chooseGuideFlavour(id) {
      if (this.adding || !this.flavour(id)?.available) return;
      this.guideFlavourId = String(id);
      if (this.config.studioEnabled) {
        this.setPreviewView("front");
        return this.addGuideFlavour();
      }
      this.renderGuide();
      if (this.root.classList.contains("is-guided-mobile")) {
        const heading = this.root.querySelector("[data-guide-bottle-heading]");
        heading?.focus?.({ preventScroll: true });
        heading?.scrollIntoView?.({ behavior: "instant", block: "start" });
      }
    }

    addGuideFlavour() {
      if (this.adding) return;
      const flavour = this.flavour(this.guideFlavourId);
      if (!flavour?.available) return this.notice("Choose an available flavour first.", "error");
      const box = this.activeBox();
      const wasReady = this.boxReady(box);
      const index = this.state.selectedSlot;
      box.slots[index] = flavour.variantId;
      this.state.selectedSlot = this.nextOpenSlot(box.slots, index);
      this.saveDraft();
      this.notice("");
      this.render();
      this.playSound(!wasReady && this.boxReady(box) ? "complete" : "select");
    }

    renderGuide() {
      this.renderGiftDeals();
      if (!this.root.querySelector("[data-guide-nav]")) return;
      const guideFocusKey = this.root.contains(document.activeElement)
        ? document.activeElement?.dataset?.focusKey || ""
        : "";
      const box = this.activeBox();
      const slotIndex = this.state.selectedSlot;
      const filledCount = box.slots.filter((id) => this.flavour(id)?.available).length;
      const selected = this.config.studioEnabled ? this.flavour(box.slots[slotIndex]) : this.flavour(this.guideFlavourId)
        || this.flavour(box.slots[slotIndex])
        || this.config.flavours.find((entry) => entry.available)
        || this.config.flavours[0];
      this.guideFlavourId = selected?.variantId || "";
      this.root.dataset.guideStep = this.guideStep;
      this.root.dataset.guideSlot = String(slotIndex + 1);
      this.root.dataset.guidePreview = String(this.guidePreviewOpen);
      const stepIndex = GUIDE_STEPS.indexOf(this.guideStep);
      const progress = this.root.querySelector("[data-guide-progress-text]");
      const studioNext = this.root.querySelector("[data-studio-next]");
      const studioPrice = this.root.querySelector(".fl-gift-studio-price");
      if (studioPrice && this.config.currency && Number.isFinite(this.config.unitPrice)) {
        const offer = this.config.currency === "ZAR" ? globalThis.FlGiftStudio?.offerQuote?.(this.config.offers, this.state.boxes.length, this.studioCartCount || 0) : null;
        const amount = offer ? offer.total : this.config.unitPrice * this.state.boxes.length;
        const price = new Intl.NumberFormat(document.documentElement.lang || "en-ZA", { style: "currency", currency: this.config.currency, currencyDisplay: "narrowSymbol" }).format(amount / 100);
        const delivery = offer ? (Number.isInteger(this.studioCartCount) && !this.studioCartHasOtherItems
          ? this.config.deliveryCopy?.short || "Courier included"
          : this.config.deliveryCopy?.checkout || "Shipping at checkout") : "";
        studioPrice.innerHTML = `<strong>${escapeHtml(price)}${offer ? "" : " total"}</strong>${delivery ? `<span>${escapeHtml(delivery)}</span>` : ""}`;
      }
      if (studioNext) {
        studioNext.textContent = ["Continue to message", "Continue to delivery", "Review my gifts", "Add gifts to cart"][stepIndex];
        studioNext.disabled = this.adding || (stepIndex === 0 && !this.boxReady(box));
      }
      if (this.config.studioEnabled && this.lastTrackedStep !== this.guideStep) {
        this.studioEvent(this.guideStep === "review" ? "review" : "step_view", { category: this.guideStep });
        this.lastTrackedStep = this.guideStep;
      }
      if (progress) progress.textContent = `Step ${stepIndex + 1} of 4 · ${this.guideStep[0].toUpperCase()}${this.guideStep.slice(1)}`;
      this.root.querySelectorAll("[data-guide-step]").forEach((button) => {
        const current = button.dataset.guideStep === this.guideStep;
        if (current) button.setAttribute("aria-current", "step");
        else button.removeAttribute("aria-current");
        button.disabled = this.adding;
      });
      this.root.querySelector("[data-guide-bottle-heading]").textContent = `Pick bottle ${slotIndex + 1}`;
      this.root.querySelector("[data-guide-bottle-help]").textContent = box.slots[slotIndex]
        ? "Choose a replacement, or continue with your lineup."
        : "Choose a flavour. We'll line it up for you.";
      this.root.querySelector("[data-guide-positions]").innerHTML = box.slots.map((id, index) => {
        const flavour = this.flavour(id);
        const active = index === slotIndex;
        const image = flavour?.image || (!this.config.studioEnabled && active ? selected?.image : "");
        return `<button type="button" class="fl-gift-builder__guide-position${active ? " is-active" : ""}${flavour ? " is-filled" : ""}" data-guide-slot="${index}" data-focus-key="guide-slot-${index}" aria-pressed="${active}" aria-label="Bottle ${index + 1}: ${escapeHtml(flavour ? this.flavourLabel(flavour.variantId) : "Not chosen")}. ${flavour ? "Change flavour" : "Choose flavour"}"${this.adding ? " disabled" : ""}>${image ? `<img src="${escapeHtml(image)}" alt="" width="60" height="80" decoding="async">` : '<span class="fl-gift-builder__guide-empty">Choose</span>'}<strong>${index + 1}</strong></button>`;
      }).join("");
      const actions = this.root.querySelector("[data-guide-slot-actions]");
      actions.innerHTML = box.slots[slotIndex]
        ? `<button type="button" data-move-slot="left" data-slot-index="${slotIndex}" data-focus-key="guide-move-left-${slotIndex}"${slotIndex === 0 || this.adding ? " disabled" : ""}>Move left</button><button type="button" data-clear-slot="${slotIndex}" data-focus-key="guide-clear-${slotIndex}"${this.adding ? " disabled" : ""}>Clear bottle ${slotIndex + 1}</button><button type="button" data-move-slot="right" data-slot-index="${slotIndex}" data-focus-key="guide-move-right-${slotIndex}"${slotIndex === SLOT_COUNT - 1 || this.adding ? " disabled" : ""}>Move right</button>`
        : "";
      const hero = this.root.querySelector("[data-guide-bottle-image]");
      if (selected?.image) {
        if (hero.getAttribute("src") !== selected.image) hero.src = selected.image;
        hero.alt = this.flavourLabel(selected.variantId);
      }
      hero.hidden = !selected?.image;
      this.root.querySelector("[data-guide-flavour-name]").textContent = selected ? this.flavourLabel(selected.variantId) : "No flavours available";
      this.root.querySelector("[data-guide-flavour-description]").textContent = selected?.description || "One of your five 200ml bottles. Pick your favourites, repeats welcome.";
      const add = this.root.querySelector("[data-guide-add-flavour]");
      add.disabled = this.adding || !selected?.available;
      add.textContent = box.slots[slotIndex] ? `Update bottle ${slotIndex + 1}` : "Add this flavour";
      const alternatives = this.root.querySelector("[data-guide-alternatives]");
      const scrollLeft = alternatives.scrollLeft;
      alternatives.innerHTML = this.config.flavours.map((entry) => `<button type="button" class="fl-gift-builder__guide-alternative" data-guide-flavour="${escapeHtml(entry.variantId)}" data-focus-key="guide-flavour-${escapeHtml(entry.variantId)}" aria-pressed="${entry.variantId === this.guideFlavourId}"${!entry.available || this.adding ? " disabled" : ""}>${entry.image ? `<img src="${escapeHtml(entry.image)}" alt="" width="90" height="120" loading="lazy" decoding="async">` : ""}<strong>${escapeHtml(this.flavourLabel(entry.variantId))}</strong>${!entry.available ? "<span>Sold out</span>" : ""}</button>`).join("");
      alternatives.scrollLeft = scrollLeft;
      this.root.querySelector("[data-guide-box-summary]").textContent = `Box ${this.state.activeIndex + 1} · ${filledCount} of 5`;
      const liveSummary = this.root.querySelector("[data-studio-live-summary]");
      if (liveSummary) liveSummary.textContent = `Box ${this.state.activeIndex + 1} · ${filledCount} of 5`;
      this.root.querySelector("[data-guide-complete]").hidden = filledCount !== SLOT_COUNT;
      this.root.querySelector('[data-guide-continue="flavours"]').hidden = filledCount !== SLOT_COUNT;
      this.root.querySelectorAll("[data-guide-continue]").forEach((button) => { button.disabled = this.adding; });
      const back = this.root.querySelector("[data-guide-back]");
      back.disabled = this.adding || (stepIndex === 0 && slotIndex === 0);
      const summary = this.root.querySelector(".fl-gift-builder__summary");
      if (this.mobileQuery?.matches && this.guideStep === "review" && summary) summary.open = true;
      if (guideFocusKey.startsWith("guide-")) {
        const preferred = this.root.querySelector(`[data-focus-key="${guideFocusKey}"]`);
        const fallback = this.root.querySelector(`[data-guide-slot="${slotIndex}"]`);
        (preferred && !preferred.disabled ? preferred : fallback)?.focus?.({ preventScroll: true });
      }
    }

    celebrateBox() {
      if (this.reducedMotion) return;
      const slots = this.root.querySelector("[data-box-slots]");
      if (!slots) return;
      globalThis.clearTimeout(this.celebrationTimer);
      slots.classList.remove("is-completing");
      void slots.offsetWidth;
      slots.classList.add("is-completing");
      this.celebrationTimer = globalThis.setTimeout(() => {
        slots.classList.remove("is-completing");
      }, 720);
    }

    measurePreviewMessage(box) {
      if (!box.message && !box.recipientLabel) {
        this.messageFitByBoxId.set(box.id, true);
        return true;
      }
      const messageNode = this.root.querySelector("[data-preview-message]");
      const label = messageNode?.closest(".fl-gift-builder__preview-message");
      const panel = messageNode?.closest("[data-preview-panel]");
      if (!messageNode || !label || panel?.hidden || label.clientHeight <= 0 || label.clientWidth <= 0) {
        this.messageFitByBoxId.set(box.id, false);
        return false;
      }

      messageNode.textContent = box.message;
      const recipientNode = this.root.querySelector("[data-preview-recipient]");
      if (recipientNode) {
        recipientNode.textContent = box.recipientLabel ? `To ${box.recipientLabel}` : "";
        recipientNode.hidden = !box.recipientLabel;
        recipientNode.classList.toggle("is-dense", box.recipientLabel.length > 40);
      }
      messageNode.style.removeProperty("font-size");
      messageNode.classList.toggle("is-dense", box.message.length > 115 && box.message.length <= 150);
      messageNode.classList.toggle("is-ultra-dense", box.message.length > 150);
      label.classList.remove("is-empty", "is-overflowing");
      label.classList.toggle("is-long", box.message.length > 120);
      label.classList.add("is-measuring");
      const labelStyle = getComputedStyle(label);
      const rowGap = Number.parseFloat(labelStyle.rowGap) || 0;
      const visibleChildren = [...label.children].filter((child) => !child.hidden);
      const reservedHeight = visibleChildren
        .filter((child) => child !== messageNode)
        .reduce((total, child) => total + child.offsetHeight, 0);
      const availableHeight = () => Math.max(
        0,
        label.clientHeight
          - reservedHeight
          - (Math.max(0, visibleChildren.length - 1) * rowGap)
          - (Number.parseFloat(labelStyle.paddingTop) || 0)
          - (Number.parseFloat(labelStyle.paddingBottom) || 0)
      );
      const overflows = () => messageNode.scrollHeight > availableHeight() + 1
        || messageNode.scrollWidth > messageNode.clientWidth + 1
        || (recipientNode && recipientNode.scrollWidth > recipientNode.clientWidth + 1);

      const fits = !overflows();
      label.classList.remove("is-measuring");
      this.messageFitByBoxId.set(box.id, fits);
      return fits;
    }

    fitPreviewMessage() {
      const fits = this.measurePreviewMessage(this.activeBox());
      this.renderPreviewMessage({ preserveFontSize: true });
      this.syncAddButtonState();
      return fits !== false;
    }

    measureAllPreviewMessages() {
      let invalidIndex = -1;
      this.state.boxes.forEach((box, index) => {
        if (this.measurePreviewMessage(box) === false && invalidIndex < 0) invalidIndex = index;
      });

      this.messageFitByBoxId.forEach((_fits, boxId) => {
        if (!this.state.boxes.some((box) => box.id === boxId)) this.messageFitByBoxId.delete(boxId);
      });
      this.renderPreviewMessage();
      this.measurePreviewMessage(this.activeBox());
      this.renderPreviewMessage({ preserveFontSize: true });
      this.syncAddButtonState();
      return { fits: invalidIndex < 0, invalidIndex };
    }

    renderPreviewMessage({ preserveFontSize = false } = {}) {
      const box = this.activeBox();
      const messageNode = this.root.querySelector("[data-preview-message]");
      const label = messageNode?.closest(".fl-gift-builder__preview-message");
      const caption = this.root.querySelector("[data-preview-message-caption]");
      const readableMessage = this.root.querySelector("[data-preview-message-readable]");
      const backMessage = this.root.querySelector("[data-preview-message-back]");
      const messageCopy = this.root.querySelector("[data-preview-message-copy]");
      const messageState = this.root.querySelector("[data-preview-message-state]");
      const hasMessage = Boolean(box.message);
      const isExample = !hasMessage && !box.recipientLabel;
      const displayMessage = isExample ? EXAMPLE_MESSAGE : box.message;
      const displayRecipient = isExample ? EXAMPLE_RECIPIENT : box.recipientLabel;
      if (messageNode) {
        if (!preserveFontSize) messageNode.style.removeProperty("font-size");
        messageNode.textContent = box.message;
      }
      ["[data-preview-recipient-back]", "[data-preview-recipient-readable]"].forEach((selector) => {
        const recipient = this.root.querySelector(selector);
        if (!recipient) return;
        recipient.textContent = displayRecipient ? `To ${displayRecipient}` : "";
        recipient.hidden = !displayRecipient;
        recipient.classList.toggle("is-dense", displayRecipient.length > 40);
      });
      if (readableMessage) readableMessage.textContent = displayMessage;
      if (backMessage) {
        backMessage.textContent = displayMessage;
        backMessage.classList.toggle("is-dense", box.message.length > 115 && box.message.length <= 150);
        backMessage.classList.toggle("is-ultra-dense", box.message.length > 150);
      }
      label?.classList.toggle("is-empty", !hasMessage);
      label?.classList.toggle("is-long", box.message.length > 120);
      label?.classList.toggle("is-overflowing", !this.boxMessageFits(box));
      messageCopy?.classList.toggle("is-empty", isExample);
      messageCopy?.classList.toggle("is-overflowing", !this.boxMessageFits(box));
      if (messageState) {
        messageState.textContent = !this.boxMessageFits(box)
          ? "Shorten this message so it fits"
          : isExample
            ? "Example label · add your own name and message"
            : "Label ready on the back";
      }
      if (caption) {
        caption.textContent = !this.boxMessageFits(box)
          ? "Shorten the message so it fits the printed label."
          : !isExample
            ? "This is how your printed label will look."
            : "Type a message in step 3 to preview the printed label.";
      }
    }

    renderPreview() {
      const box = this.activeBox();
      const boxLabel = this.root.querySelector("[data-preview-box-label]");
      if (boxLabel) boxLabel.textContent = `Previewing Box ${this.state.activeIndex + 1}`;

      const boxWindows = this.root.querySelector("[data-preview-box-windows]");
      if (boxWindows) {
        boxWindows.innerHTML = box.slots.map((variantId, index) => {
          const flavour = this.flavour(variantId);
          const image = flavour?.image ? `<img src="${escapeHtml(flavour.image)}" alt="" decoding="async">` : "";
          return `<span class="fl-gift-builder__preview-box-window${flavour ? " is-selected" : ""}" data-preview-box-window="${index + 1}">${image}</span>`;
        }).join("");
      }

      const flavours = this.root.querySelector("[data-preview-flavours]");
      if (flavours) {
        flavours.innerHTML = box.slots.map((variantId, index) => {
          const flavour = this.flavour(variantId);
          return `<li class="fl-gift-builder__preview-flavour${flavour ? " is-selected" : ""}"><span class="fl-gift-builder__preview-position">${index + 1}</span>${flavour?.image ? `<img src="${escapeHtml(flavour.image)}" alt="" loading="lazy">` : ""}<span>${escapeHtml(flavour ? this.flavourLabel(flavour.variantId) : "Choose a flavour")}</span></li>`;
        }).join("");
      }

      this.renderPreviewMessage();
    }

    openFlavourPicker(index) {
      const slotIndex = clamp(index, 0, SLOT_COUNT - 1);
      const dialog = this.root.querySelector("[data-flavour-dialog]");
      if (!dialog?.showModal) return;

      this.flavourPickerReturnKey = `slot-${slotIndex}`;
      const title = this.root.querySelector("[data-flavour-dialog-title]");
      if (title) title.textContent = `Choose a flavour for position ${slotIndex + 1}`;
      if (!dialog.open) dialog.showModal();

      const currentId = this.activeBox().slots[slotIndex];
      const preferred = currentId
        ? dialog.querySelector(`[data-flavour-id="${CSS.escape(currentId)}"]`)
        : dialog.querySelector("[data-flavour-id]:not(:disabled)");
      preferred?.focus?.({ preventScroll: true });
    }

    closeFlavourPicker() {
      const dialog = this.root.querySelector("[data-flavour-dialog]");
      if (dialog?.open) {
        dialog.close();
        return;
      }
      this.restoreFlavourPickerFocus();
    }

    restoreFlavourPickerFocus() {
      const focusKey = this.flavourPickerReturnKey;
      this.flavourPickerReturnKey = "";
      if (!focusKey) return;
      this.root.querySelector(`[data-focus-key="${focusKey}"]`)?.focus?.({ preventScroll: true });
    }

    selectGiftDeal(count, { fromLink = false } = {}) {
      if (!this.config.studioEnabled || ![1, 2, 3, 5, 15].includes(count) || this.adding || this.generating) return false;
      if (this.state.pendingCart) {
        this.notice("Check the previous cart submission before choosing another deal.", "error");
        return false;
      }
      if (!this.config.product.available || !Number.isInteger(this.studioCartCount)) {
        this.notice("Your cart must be checked before choosing a deal. Reload and try again.", "error");
        return false;
      }
      if (count > this.config.maxBoxes || count + this.studioCartCount > 25) {
        this.notice("This deal would exceed 25 boxes in your cart. Review your cart first.", "error");
        return false;
      }
      const removed = this.state.boxes.slice(count);
      if (removed.some((box) => this.boxHasContent(box))) {
        if (fromLink) {
          this.notice(`Your saved ${this.state.boxes.length}-box draft is here. Choose the ${count}-box deal below to replace the extra boxes.`, "info");
          return false;
        }
        if (!window.confirm(`Switch to ${count} ${count === 1 ? "box" : "boxes"}? This removes the last ${removed.length} saved boxes and their messages.`)) return false;
      }
      if (!this.config.flavours.some((flavour) => flavour.available)) {
        this.notice("Flavours are currently unavailable. Please try again later.", "error");
        return false;
      }
      this.setCount(count);
      this.state.activeIndex = 0;
      this.state.selectedSlot = 0;
      this.saveDraft();
      this.render();
      this.setGuideStep("flavours");
      this.notice(`${count} ${count === 1 ? "gift box" : "gift boxes"} selected. Choose five flavours for each box, or start with a favourite mix.`, "success");
      return true;
    }

    renderGiftDeals() {
      const copy = this.config.deliveryCopy || {};
      const mixedCart = Number.isInteger(this.studioCartCount) && this.studioCartHasOtherItems;
      const deliveryTitle = this.root.querySelector("[data-gift-delivery-title]");
      const deliveryDetail = this.root.querySelector("[data-gift-delivery-detail]");
      if (deliveryTitle) deliveryTitle.textContent = mixedCart ? copy.normal || "Normal courier rates apply" : copy.included || "Courier delivery included";
      if (deliveryDetail) deliveryDetail.textContent = mixedCart ? copy.mixed || "Your cart also has other products. Shipping is calculated at checkout." : copy.giftOnly || "Gift-box-only orders · One South African address";
      this.root.querySelectorAll("[data-gift-deal]").forEach((button) => {
        const count = Number(button.dataset.giftDeal);
        const checked = Number.isInteger(this.studioCartCount);
        const quote = this.config.currency === "ZAR" && checked
          ? globalThis.FlGiftStudio?.offerQuote?.(this.config.offers, count, this.studioCartCount) : null;
        const label = button.querySelector("[data-deal-price]");
        if (label) label.textContent = !checked ? "Checking cart…" : quote
          ? new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR" }).format(quote.total / 100)
          : this.config.currency === "ZAR" && this.config.offers?.enabled ? "Check cart" : "Choose & customise";
        const delivery = button.querySelector("[data-deal-delivery]");
        if (delivery) {
          delivery.hidden = !quote;
          delivery.textContent = mixedCart ? copy.plusShipping || "+ shipping" : copy.short || "Courier included";
        }
        button.disabled = Boolean(this.adding || this.generating || this.state.pendingCart || !checked || !this.config.product.available
          || count > this.config.maxBoxes || count + this.studioCartCount > 25);
        button.setAttribute("aria-pressed", String(this.state.boxes.length === count));
      });
    }

    setCount(nextCount) {
      const count = clamp(nextCount, 1, this.config.maxBoxes);
      while (this.state.boxes.length < count) {
        this.state.boxes.push(createBox(this.config.messageLimit));
      }
      if (this.state.boxes.length > count) {
        this.state.boxes = this.state.boxes.slice(0, count);
      }
      this.state.activeIndex = clamp(this.state.activeIndex, 0, this.state.boxes.length - 1);
      this.suggestions = [];
      this.saveDraft();
      this.render();
    }

    presetFlavours(preset) {
      return preset.skus.map((sku) => this.config.flavours.find((flavour) => flavour.sku === sku));
    }

    randomizeFlavours() {
      if (this.adding || this.generating || !this.config.product.available) return;
      const available = [...new Set(this.config.flavours.filter((flavour) => flavour.available).map((flavour) => flavour.variantId))];
      if (!available.length) return;
      const box = this.activeBox();
      const before = [...box.slots];
      // Shuffle without replacement first; repeat balanced rounds only when stock is limited.
      const shuffled = [...available];
      for (let index = shuffled.length - 1; index > 0; index -= 1) {
        const other = Math.floor(Math.random() * (index + 1));
        [shuffled[index], shuffled[other]] = [shuffled[other], shuffled[index]];
      }
      const after = Array.from({ length: SLOT_COUNT }, (_, index) => shuffled[index % shuffled.length]);
      if (available.length > SLOT_COUNT && after.every((id) => before.includes(id))) {
        // A repeat click should change the selection, not merely rearrange the same bottles.
        const alternatives = shuffled.filter((id) => !before.includes(id));
        after[Math.floor(Math.random() * SLOT_COUNT)] = alternatives[Math.floor(Math.random() * alternatives.length)];
      } else if (after.every((id, index) => id === before[index])) {
        if (available.length === 1) return;
        after.push(after.shift());
      }
      const previous = this.randomMixByBoxId.get(box.id);
      this.randomMixByBoxId.set(box.id, { before, after: [...after], count: (previous?.count || 0) + 1 });
      box.slots = after;
      this.state.selectedSlot = 0;
      this.guideFlavourId = after[0];
      this.suggestions = [];
      this.notice("");
      this.saveDraft();
      this.render();
      this.playSound("complete");
    }

    undoRandomizeFlavours() {
      if (this.adding || this.generating) return;
      const box = this.activeBox();
      const previous = this.randomMixByBoxId.get(box.id);
      if (!previous || !previous.after.every((id, index) => id === box.slots[index])) return;
      box.slots = [...previous.before];
      this.randomMixByBoxId.delete(box.id);
      this.state.selectedSlot = Math.max(0, box.slots.findIndex((id) => !this.flavour(id)?.available));
      this.guideFlavourId = box.slots[this.state.selectedSlot] || "";
      this.suggestions = [];
      this.notice("");
      this.saveDraft();
      this.render();
      const status = this.root.querySelector("[data-randomize-status]");
      if (status) status.textContent = "Previous selection restored. Make it yours, one bottle at a time.";
      this.root.querySelector("[data-randomize-flavours]")?.focus?.({ preventScroll: true });
    }

    renderRandomizer() {
      const button = this.root.querySelector("[data-randomize-flavours]");
      if (!button) return; // Support cached markup during a theme asset rollout.
      const box = this.activeBox();
      const currentBoxIds = new Set(this.state.boxes.map((entry) => entry.id));
      for (const id of this.randomMixByBoxId.keys()) {
        if (!currentBoxIds.has(id)) this.randomMixByBoxId.delete(id);
      }
      let previous = this.randomMixByBoxId.get(box.id);
      if (previous && !previous.after.every((id, index) => id === box.slots[index])) {
        this.randomMixByBoxId.delete(box.id);
        previous = null;
      }
      const available = this.config.flavours.filter((flavour) => flavour.available);
      const onlyMix = available.length === 1 && box.slots.every((id) => id === available[0].variantId);
      button.disabled = this.adding || this.generating || !this.config.product.available || !available.length || onlyMix;
      const label = this.root.querySelector("[data-randomize-label]");
      if (label) label.textContent = previous ? "Shuffle again" : "Surprise me";
      const scope = this.root.querySelector("[data-randomize-scope]");
      if (scope) scope.textContent = this.state.boxes.length > 1 ? `Mix it up for Box ${this.state.activeIndex + 1}` : "Find your flavour mix";
      const undo = this.root.querySelector("[data-undo-randomize]");
      if (undo) {
        undo.hidden = !previous;
        undo.disabled = this.adding || this.generating;
      }
      const status = this.root.querySelector("[data-randomize-status]");
      if (status) status.textContent = !this.config.product.available
        ? "Gift packs are currently sold out."
        : !available.length
          ? "No flavours are available right now. Please check back soon."
          : onlyMix
            ? `Only ${available[0].name} is available, so all five bottles use it.`
            : previous
              ? `Mix ${previous.count} ready. ${available.length >= SLOT_COUNT ? "Five different flavours." : "Available flavours may repeat."} Swap any bottle or shuffle again.`
              : available.length >= SLOT_COUNT
                ? "Five different flavours. Picked at random. Yours to perfect."
                : "We’ll fill all five bottles from the available flavours; some will repeat.";
    }

    applyPreset(id) {
      if (this.adding) return;
      const preset = (this.config.presets || []).find((entry) => entry.id === id);
      if (!preset) return;
      const flavours = this.presetFlavours(preset);
      if (flavours.length !== SLOT_COUNT || flavours.some((flavour) => !flavour?.available)) {
        return this.notice("This combination has a sold-out flavour. Please choose another pack or build your own.", "error");
      }
      this.activeBox().slots = flavours.map((flavour) => flavour.variantId);
      this.state.selectedSlot = 0;
      this.saveDraft();
      this.notice(`${preset.name} selected. You can still change any bottle.`);
      this.render();
    }

    renderPresets() {
      const container = this.root.querySelector("[data-gift-presets]");
      if (!container) return;
      const colours = { FL002: "#8BAF84", FL008: "#DA291C", FL014: "#FF8200", FL026: "#722F37", FL035: "#7E22CE", FL038: "#A1C935", FL041: "#40B2FF", FL071: "#FFC72C" };
      container.innerHTML = (this.config.presets || []).map((preset) => {
        const flavours = this.presetFlavours(preset);
        const available = flavours.length === SLOT_COUNT && flavours.every((flavour) => flavour?.available);
        const selected = flavours.every((flavour, index) => flavour && this.activeBox().slots[index] === flavour.variantId);
        return `<button type="button" class="fl-gift-builder__preset" data-gift-preset="${escapeHtml(preset.id)}" data-focus-key="preset-${escapeHtml(preset.id)}" aria-pressed="${selected}"${!available || this.adding ? " disabled" : ""}><strong>${escapeHtml(preset.name)}</strong><span class="fl-gift-builder__preset-lineup">${flavours.map((flavour, index) => `<span style="--preset-colour:${colours[preset.skus[index]] || '#888'}">${index + 1}. ${escapeHtml(flavour ? this.flavourLabel(flavour.variantId) : preset.skus[index])}</span>`).join("")}</span>${!available ? '<span>Currently unavailable</span>' : ''}</button>`;
      }).join("");
    }

    bind() {
      this.root.addEventListener("click", (event) => {
        if (event.target.matches?.("[data-flavour-dialog]")) {
          return this.closeFlavourPicker();
        }
        const target = event.target.closest("button");
        if (!target) return;
        if (target.dataset.giftDeal) return this.selectGiftDeal(Number(target.dataset.giftDeal));
        if (target.dataset.previewView) return this.setPreviewView(target.dataset.previewView);
        if (target.matches("[data-studio-next]")) return this.guideStep === "review" ? this.addToCart() : this.setGuideStep(GUIDE_STEPS[GUIDE_STEPS.indexOf(this.guideStep) + 1]);
        if (target.matches("[data-randomize-flavours]")) return this.randomizeFlavours();
        if (target.matches("[data-undo-randomize]")) return this.undoRandomizeFlavours();
        if (target.dataset.giftPreset) return this.applyPreset(target.dataset.giftPreset);

        if (target.dataset.guideStep) return this.setGuideStep(target.dataset.guideStep);
        if (target.dataset.guideEdit) return this.setGuideStep(target.dataset.guideEdit, { allowIncomplete: true });
        if (target.dataset.guideContinue) return this.setGuideStep(GUIDE_STEPS[GUIDE_STEPS.indexOf(target.dataset.guideContinue) + 1]);
        if (target.dataset.guideSlot != null) return this.selectGuideSlot(target.dataset.guideSlot);
        if (target.dataset.guideFlavour) return this.chooseGuideFlavour(target.dataset.guideFlavour);
        if (target.matches("[data-guide-add-flavour]")) return this.addGuideFlavour();
        if (target.matches("[data-guide-manage-boxes]")) return this.setGuideStep("review", { allowIncomplete: true });
        if (target.matches("[data-guide-preview-open], [data-guide-preview-close]")) {
          this.guidePreviewOpen = target.matches("[data-guide-preview-open]");
          this.renderGuide();
          return this.focusGuideStart();
        }
        if (target.matches("[data-guide-back]")) {
          if (this.guideStep === "flavours") return this.selectGuideSlot(this.state.selectedSlot - 1);
          return this.setGuideStep(GUIDE_STEPS[GUIDE_STEPS.indexOf(this.guideStep) - 1], { allowIncomplete: true });
        }

        if (target.matches("[data-flavour-picker-close]")) {
          return this.closeFlavourPicker();
        }

        if (target.matches("[data-gift-sound-toggle]")) {
          return this.toggleSound();
        }

        if (target.matches("[data-box-count-minus]")) {
          return this.setCount(this.state.boxes.length - 1);
        }
        if (target.matches("[data-box-count-plus]")) {
          return this.setCount(this.state.boxes.length + 1);
        }
        if (target.dataset.boxTab != null) {
          this.state.activeIndex = clamp(target.dataset.boxTab, 0, this.state.boxes.length - 1);
          this.state.selectedSlot = Math.max(0, this.activeBox().slots.findIndex((id) => !this.flavour(id)?.available));
          this.guideFlavourId = this.activeBox().slots[this.state.selectedSlot] || "";
          this.suggestions = [];
          this.saveDraft();
          this.render();
          this.playSound("select");
          if (this.config.studioEnabled && target.hasAttribute("data-studio-edit-box")) return this.setGuideStep("flavours", { allowIncomplete: true });
          return;
        }
        if (target.matches("[data-reset-active-box]")) return this.resetActiveBox();
        if (target.matches("[data-reset-all-boxes]")) return this.resetAllBoxes();
        if (target.dataset.selectSlot != null) {
          const slotIndex = clamp(target.dataset.selectSlot, 0, SLOT_COUNT - 1);
          this.state.selectedSlot = slotIndex;
          this.saveDraft();
          this.render();
          this.playSound("select");
          return this.openFlavourPicker(slotIndex);
        }
        if (target.dataset.flavourId) {
          const flavour = this.flavour(target.dataset.flavourId);
          if (!flavour?.available) {
            return this.notice(`${flavour ? this.flavourLabel(flavour.variantId) : "That flavour"} is currently sold out.`, "error");
          }
          const box = this.activeBox();
          const wasReady = this.boxReady(box);
          const targetIndex = this.state.selectedSlot;
          box.slots[targetIndex] = flavour.variantId;
          this.state.selectedSlot = this.nextOpenSlot(box.slots, targetIndex);
          this.saveDraft();
          this.render();
          this.closeFlavourPicker();
          if (!wasReady && this.boxReady(box)) {
            this.playSound("complete");
            this.celebrateBox();
          } else {
            this.playSound("select");
          }
          return;
        }
        if (target.dataset.clearSlot != null) {
          const index = Number(target.dataset.clearSlot);
          this.activeBox().slots[index] = "";
          this.state.selectedSlot = clamp(index, 0, SLOT_COUNT - 1);
          this.guideFlavourId = "";
          this.saveDraft();
          this.render();
          this.playSound("remove");
          return;
        }
        if (target.dataset.moveSlot) {
          const from = Number(target.dataset.slotIndex);
          const to = target.dataset.moveSlot === "left" ? from - 1 : from + 1;
          if (to >= 0 && to < SLOT_COUNT) {
            const slots = this.activeBox().slots;
            [slots[from], slots[to]] = [slots[to], slots[from]];
            this.state.selectedSlot = to;
            this.guideFlavourId = slots[to];
            this.saveDraft();
            this.render();
            this.playSound("move");
            return;
          }
        }
        if (target.matches("[data-apply-layout-all]")) return this.applyLayoutToAll();
        if (target.matches("[data-apply-message-all]")) return this.applyMessageToAll();
        if (target.matches("[data-apply-current-all]")) return this.applyCurrentToAll();
        if (target.matches("[data-generate-message]")) return this.generateMessages();
        if (target.dataset.messageSuggestion != null) {
          const box = this.activeBox();
          box.message = normalizeMessage(
            this.suggestions[Number(target.dataset.messageSuggestion)],
            this.config.messageLimit
          );
          this.invalidateMessageFit(box);
          this.saveDraft();
          return this.render();
        }
        if (target.matches("[data-add-gift-boxes]")) return this.addToCart();
      });

      this.root.querySelector("[data-flavour-dialog]")?.addEventListener("close", () => {
        this.restoreFlavourPickerFocus();
      });

      this.root.addEventListener("change", (event) => {
        const target = event.target;
        if (target.matches("[data-box-count]")) return this.setCount(target.value);
        if (target.matches("[data-preferred-delivery-date]")) {
          const preferredDeliveryDate = normalizeDeliveryDate(target.value);
          if (target.value && (!preferredDeliveryDate || preferredDeliveryDate < todayIso())) {
            target.setAttribute("aria-invalid", "true");
            target.value = this.state.preferredDeliveryDate || "";
            return this.notice("Choose today or a future preferred delivery date.", "error");
          }
          target.setAttribute("aria-invalid", "false");
          this.state.preferredDeliveryDate = preferredDeliveryDate;
          this.saveDraft();
          this.render();
          return this.notice(
            preferredDeliveryDate
              ? "Preferred delivery date saved as a request. Courier delivery is not guaranteed."
              : "Preferred delivery date removed.",
            "success"
          );
        }
        if (target.dataset.slotSelect != null) {
          const flavour = target.value ? this.flavour(target.value) : null;
          if (target.value && !flavour?.available) {
            target.value = this.activeBox().slots[Number(target.dataset.slotSelect)] || "";
            return this.notice(`${flavour ? this.flavourLabel(flavour.variantId) : "That flavour"} is currently sold out.`, "error");
          }
          this.activeBox().slots[Number(target.dataset.slotSelect)] = String(target.value || "");
          this.state.selectedSlot = clamp(target.dataset.slotSelect, 0, SLOT_COUNT - 1);
          this.saveDraft();
          this.render();
          this.playSound(target.value ? "select" : "remove");
          return;
        }
        if (target.dataset.briefField) this.saveBriefField(target);
      });

      this.root.addEventListener("input", (event) => {
        const target = event.target;
        if (target.dataset?.briefField) return this.saveBriefField(target);
        if (target.matches("[data-gift-message]")) {
          const message = normalizeMessage(target.value, this.config.messageLimit);
          if (target.value !== message) target.value = message;
          const box = this.activeBox();
          box.message = message;
          this.invalidateMessageFit(box);
          this.suggestions = [];
          this.saveDraft();
          this.updateMessageCount();
          this.renderPreviewMessage();
          this.fitPreviewMessage();
        }
      });
    }

    saveBriefField(target) {
      const field = target.dataset.briefField;
      const box = this.activeBox();
      const brief = box.brief;
      if (!Object.prototype.hasOwnProperty.call(brief, field)) return;
      const maxLength = field === "context" ? 320 : ["tone", "language"].includes(field) ? 40 : 80;
      brief[field] = normalizeText(target.value, maxLength);
      if (field === "recipient") {
        box.recipientLabel = brief.recipient;
        this.invalidateMessageFit(box);
        this.renderPreviewMessage();
        this.fitPreviewMessage();
      }
      this.suggestions = [];
      this.saveDraft();
    }

    applyLayoutToAll() {
      const source = [...this.activeBox().slots];
      this.state.boxes.forEach((box) => {
        box.slots = [...source];
      });
      this.saveDraft();
      this.render();
      this.notice("Flavours and positions applied to all boxes.", "success");
    }

    applyMessageToAll() {
      const source = this.activeBox();
      this.state.boxes.forEach((box) => {
        box.message = source.message;
        box.brief = { ...source.brief, recipient: box.recipientLabel };
        this.invalidateMessageFit(box);
      });
      this.saveDraft();
      this.render();
      this.notice("Message applied to all boxes.", "success");
    }

    applyCurrentToAll() {
      const source = this.activeBox();
      this.state.boxes.forEach((box) => {
        box.slots = [...source.slots];
        box.message = source.message;
        box.brief = { ...source.brief, recipient: box.recipientLabel };
        this.invalidateMessageFit(box);
      });
      this.saveDraft();
      this.render();
      this.notice("The complete current box was applied to all boxes.", "success");
    }

    resetActiveBox() {
      if (this.adding || this.generating) return;
      const index = this.state.activeIndex;
      if (!this.boxHasContent(this.activeBox())) {
        return this.notice(`Box ${index + 1} is already empty.`, "info");
      }
      if (!window.confirm(`Clear Box ${index + 1}? This removes its flavours, message and message details.`)) return;

      this.state.boxes[index] = createBox(this.config.messageLimit);
      this.state.selectedSlot = 0;
      this.suggestions = [];
      this.saveDraft();
      this.render();
      this.notice(`Box ${index + 1} was cleared.`, "success");
    }

    resetAllBoxes() {
      if (this.adding || this.generating) return;
      const count = this.state.boxes.length;
      const hasContent = this.state.boxes.some((box) => this.boxHasContent(box))
        || Boolean(this.state.preferredDeliveryDate);
      if (!hasContent) return this.notice("All boxes are already empty.", "info");
      if (!window.confirm(`Clear all ${count} ${count === 1 ? "box" : "boxes"}? This removes every flavour, message, message detail and the preferred delivery date. The box count will stay the same.`)) return;

      this.state.boxes = Array.from(
        { length: count },
        () => createBox(this.config.messageLimit)
      );
      this.state.activeIndex = 0;
      this.state.selectedSlot = 0;
      this.state.preferredDeliveryDate = "";
      this.suggestions = [];
      this.messageFitByBoxId.clear();
      this.saveDraft();
      this.render();
      this.notice(`All ${count} ${count === 1 ? "box" : "boxes"} were cleared.`, "success");
    }

    selectedFlavourNames() {
      return this.activeBox().slots
        .map((id) => this.flavourName(id))
        .filter((name) => name !== "Not selected");
    }

    async generateMessages() {
      if (this.generating) return;
      const box = this.activeBox();
      const hasBrief = box.brief.recipient
        || box.brief.occasion
        || box.brief.sender
        || box.brief.context
        || box.message
        || box.slots.some(Boolean);
      if (!hasBrief) {
        return this.notice("Add a recipient, occasion, note, message or flavour first.", "error", { focus: true });
      }

      this.generating = true;
      this.render();
      let failed = false;
      try {
        const response = await fetch(this.config.messageEndpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            ...box.brief,
            currentDraft: box.message,
            flavours: this.selectedFlavourNames()
          })
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !Array.isArray(payload.suggestions)) {
          throw new Error(payload.message || "Message generation failed.");
        }
        this.suggestions = payload.suggestions.slice(0, 3);
        this.notice(
          payload.source === "ai"
            ? "Three AI message ideas are ready."
            : "Three message ideas are ready.",
          "success"
        );
      } catch (error) {
        failed = true;
        this.notice(
          error.message || "Message ideas are temporarily unavailable. You can still type your own message.",
          "error"
        );
      } finally {
        this.generating = false;
        this.render();
        if (failed) this.focusGuideNotice();
      }
    }

    componentManifest(box) {
      return box.slots.map((variantId, slotIndex) => {
        const flavour = this.flavour(variantId);
        return {
          position: slotIndex + 1,
          variantId: String(variantId),
          sku: flavour?.sku || "",
          name: flavour?.name || ""
        };
      });
    }

    cartItems() {
      const groupId = this.state.groupId;
      const preferredDeliveryDate = normalizeDeliveryDate(this.state.preferredDeliveryDate);
      const formattedDeliveryDate = formatDeliveryDate(preferredDeliveryDate);
      return this.state.boxes.map((box, index) => ({
        id: Number(this.config.product.variantId),
        quantity: 1,
        properties: {
          "Gift box": `Box ${index + 1} of ${this.state.boxes.length}`,
          "Position 1": this.flavourName(box.slots[0]),
          "Position 2": this.flavourName(box.slots[1]),
          "Position 3": this.flavourName(box.slots[2]),
          "Position 4": this.flavourName(box.slots[3]),
          "Position 5": this.flavourName(box.slots[4]),
          ...(box.message ? { "Gift message": box.message } : {}),
          ...(box.recipientLabel ? { "Gift recipient": box.recipientLabel } : {}),
          ...(preferredDeliveryDate ? {
            "Preferred delivery date (request only)": formattedDeliveryDate,
            _flss_preferred_delivery_date: preferredDeliveryDate
          } : {}),
          _flss_gift_box_id: box.id,
          _flss_gift_box_group_id: groupId,
          _flss_gift_box_schema: "flss.gift-box.v1",
          _flss_gift_box_sequence: String(index + 1),
          _flss_gift_box_count: String(this.state.boxes.length),
          _flss_gift_box_variant_ids: box.slots.join(","),
          _flss_gift_box_components: JSON.stringify(this.componentManifest(box)),
          ...(box.message ? { _flss_gift_box_message: box.message } : {}),
          _flss_gift_message_language: box.brief.language || "English"
        }
      }));
    }

    prepareStudioPrivacy() {
      if (!this.config.studioEnabled) return;
      const shopify = globalThis.Shopify;
      if (typeof shopify?.customerPrivacy?.analyticsProcessingAllowed === "function" || typeof shopify?.loadFeatures !== "function") return;
      if (Date.now() < (this.studioPrivacyRetryAt || 0)) return;
      this.studioPrivacyRetryAt = Date.now() + 10000;
      // Themes do not necessarily load the consent API. Initialize it without
      // changing permission or replaying interactions made before it was ready.
      // A later interaction can retry a failed/stalled load; commerce never waits.
      try { shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.2" }], () => {}); } catch { /* Analytics is optional. */ }
    }

    studioEvent(name, details = {}) {
      if (!this.config.studioEnabled) return;
      // A fixed diagnostic state helps preview QA distinguish missing consent
      // from a failed publisher without exposing event payloads or gift data.
      const status = (value) => { if (this.root?.dataset) this.root.dataset.studioAnalytics = value; };
      this.prepareStudioPrivacy();
      if (typeof globalThis.Shopify?.customerPrivacy?.analyticsProcessingAllowed !== "function") return status("privacy_unavailable");
      try { if (globalThis.Shopify.customerPrivacy.analyticsProcessingAllowed() !== true) return status("consent_denied"); } catch { return status("privacy_error"); }
      if (!STUDIO_EVENTS.has(name)) return;
      if (typeof globalThis.Shopify?.analytics?.publish !== "function") return status("publisher_unavailable");
      if (name === "builder_start" && this.studioStarted) return;
      if (name === "builder_start") this.studioStarted = true;
      // Only anonymous counts/categories; never pass draft contents, IDs or messages.
      const payload = { audience: this.studioAudience === "team" ? "team" : "personal" };
      if (Number.isInteger(details.count) && details.count >= 0 && details.count <= 25) payload.count = details.count;
      if (STUDIO_CATEGORIES.has(details.category)) payload.category = details.category;
      const failed = () => { status("publish_failed"); if (name === "builder_start") this.studioStarted = false; };
      try {
        status("publish_pending");
        const result = globalThis.Shopify.analytics.publish(`gift_studio_${name}`, payload);
        // Publishing can reject asynchronously. A failed start may be retried by
        // the next interaction, but analytics must never interrupt the builder.
        if (result && typeof result.then === "function") result.then(sent => { if (sent === false) failed(); else status("published"); }).catch(failed);
        else if (result === false) failed();
        else status("published");
      } catch { failed(); }
    }

    async readCartCapacity() {
      const response = await fetch(this.config.cartReadUrl, { headers: { Accept: "application/json" }, cache: "no-store" });
      if (!response.ok) throw new Error("Cart capacity could not be checked. Please try again before adding gifts.");
      const cart = await response.json();
      const { count, hasOtherItems } = globalThis.FlGiftStudio.cartSummary(cart, this.config.product.variantId);
      if (this.config.studioEnabled) this.reconcileStudioDraft(cart);
      this.studioCartCount = count;
      this.studioCartHasOtherItems = hasOtherItems;
      const node = this.root.querySelector("[data-cart-capacity]");
      if (node) node.textContent = `${count} gift boxes already in your cart. ${Math.max(0, 25 - count)} places remaining. One delivery address per order.${hasOtherItems ? " Other products are in your cart, so normal shipping applies." : ""}`;
      return count;
    }

    async addToCart() {
      if (this.adding) return;
      if (this.config.studioEnabled && this.state.pendingCart) {
        this.studioEvent("cart_error", { category: "draft_pending", count: this.state.boxes.length });
        return this.notice("Your previous submission may already be in the cart. Open your cart and verify it before starting a new gift order; this draft will not be sent twice.", "error", { focus: true });
      }
      if (!this.config.product.available) {
        this.studioEvent("cart_error", { category: "sold_out", count: this.state.boxes.length });
        return this.notice("This gift box is currently sold out.", "error", { focus: true });
      }
      if (!this.state.boxes.every((box) => this.boxReady(box))) {
        this.studioEvent("cart_error", { category: "incomplete_box", count: this.state.boxes.length });
        return this.notice("Complete all five available flavour positions in every box first.", "error", { focus: true });
      }
      const preferredDeliveryDate = normalizeDeliveryDate(this.state.preferredDeliveryDate);
      if (this.state.preferredDeliveryDate && (
        !preferredDeliveryDate || preferredDeliveryDate < todayIso()
      )) {
        this.studioEvent("cart_error", { category: "delivery_date", count: this.state.boxes.length });
        this.root.querySelector("[data-preferred-delivery-date]")?.setAttribute("aria-invalid", "true");
        return this.notice("Choose today or a future preferred delivery date.", "error", { focus: true });
      }
      const messageValidation = this.measureAllPreviewMessages();
      if (!messageValidation.fits) {
        this.studioEvent("cart_error", { category: "message_fit", count: this.state.boxes.length });
        this.state.activeIndex = messageValidation.invalidIndex;
        this.render();
        return this.notice(
          `Shorten the message in Box ${messageValidation.invalidIndex + 1} so it fits the printed label.`,
          "error",
          { focus: true }
        );
      }

      this.adding = true;
      this.render();
      let failed = false;
      let errorCategory = "cart_check";
      try {
        if (this.config.studioEnabled) {
          const groupId = this.state.groupId;
          const existing = await this.readCartCapacity();
          if (this.state.groupId !== groupId) throw new Error("Your cart changed. Choose the flavours for your new gift before adding it.");
          errorCategory = "capacity";
          globalThis.FlGiftImport.assertCapacity(this.state.boxes.length, 0, existing);
          errorCategory = "draft_storage";
          this.state.pendingCart = true;
          this.saveDraft();
        }
        errorCategory = "cart_uncertain";
        const response = await fetch(this.config.cartAddUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: this.cartItems() })
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          if (this.config.studioEnabled && response.status >= 400 && response.status < 500) {
            errorCategory = "shopify_rejection";
            this.state.pendingCart = false;
            this.saveDraft();
          }
          throw new Error(cartErrorMessage(payload, response.status));
        }
        if (this.config.studioEnabled && (!Array.isArray(payload.items) || payload.items.length === 0)) {
          throw new Error("Shopify did not confirm the added items. Check your cart before starting another gift draft.");
        }
        const addedCount = this.state.boxes.length;
        this.studioEvent("cart_added", { count: addedCount });
        // Clear the in-memory copy too: browser Back can restore this page from
        // its navigation cache without rerunning the constructor.
        if (this.config.studioEnabled) this.resetStudioDraft({ cartHadItems: true });
        else this.discardSavedDraft();
        if (this.config.redirectToCart) {
          window.location.assign(this.config.cartUrl);
        } else {
          document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
          this.notice(
            `${addedCount} customised ${addedCount === 1 ? "box was" : "boxes were"} added to your cart.`,
            "success"
          );
        }
      } catch (error) {
        failed = true;
        this.studioEvent("cart_error", { category: errorCategory, count: this.state.boxes.length });
        this.notice(error.message || "The gift boxes could not be added to your cart.", "error");
      } finally {
        this.adding = false;
        this.render();
        if (failed) this.focusGuideNotice();
      }
    }

    notice(message, type = "info", { focus = false } = {}) {
      const node = this.root.querySelector("[data-builder-notice]");
      if (!node) return;
      node.textContent = message;
      node.dataset.type = type;
      const guideNode = this.root.querySelector("[data-guide-notice]");
      if (guideNode) {
        guideNode.textContent = message;
        guideNode.dataset.type = type;
      }
      if (focus) this.focusGuideNotice();
    }

    updateMessageCount() {
      const node = this.root.querySelector("[data-message-count]");
      if (node) {
        node.textContent = `${this.activeBox().message.length} / ${this.config.messageLimit}`;
      }
    }

    render() {
      const recovery = this.root.querySelector("[data-gift-cart-recovery]");
      if (recovery) recovery.hidden = !this.state.pendingCart;
      const activeFocusKey = this.root.contains(document.activeElement)
        ? document.activeElement?.dataset?.focusKey || ""
        : "";
      const box = this.activeBox();
      const filledCount = box.slots.filter(Boolean).length;
      const hasMultipleBoxes = this.state.boxes.length > 1;
      const countInput = this.root.querySelector("[data-box-count]");
      if (countInput) countInput.value = this.state.boxes.length;
      this.renderPresets();
      this.renderRandomizer();
      this.syncSoundControl();
      this.renderPreview();
      this.measureAllPreviewMessages();

      const resetActive = this.root.querySelector("[data-reset-active-box]");
      if (resetActive) {
        resetActive.textContent = `Reset Box ${this.state.activeIndex + 1}`;
        resetActive.disabled = this.adding || this.generating || !this.boxHasContent(box);
      }
      const resetAll = this.root.querySelector("[data-reset-all-boxes]");
      if (resetAll) {
        resetAll.hidden = !hasMultipleBoxes;
        resetAll.disabled = this.adding
          || this.generating
          || !(this.state.preferredDeliveryDate || this.state.boxes.some((entry) => this.boxHasContent(entry)));
      }

      ["[data-apply-layout-all]", "[data-apply-message-all]", "[data-apply-current-all]"]
        .forEach((selector) => {
          const control = this.root.querySelector(selector);
          if (control) control.hidden = !hasMultipleBoxes;
        });

      const tabs = this.root.querySelector("[data-box-tabs]");
      tabs.hidden = !hasMultipleBoxes;
      tabs.innerHTML = this.state.boxes.map((entry, index) => {
        const ready = this.boxReady(entry);
        const messageFits = this.boxMessageFits(entry);
        const active = index === this.state.activeIndex;
        const status = (entry.recipientLabel ? `${entry.recipientLabel} · ` : "") + (!messageFits
          ? "Message too long"
          : ready ? "Complete" : `${entry.slots.filter(Boolean).length}/5 filled`);
        return `<button type="button" aria-pressed="${active}" aria-label="Box ${index + 1}: ${status}${active ? ". Currently editing" : ""}" class="fl-gift-builder__box-tab${active ? " is-active" : ""}${ready ? " is-ready" : ""}${messageFits ? "" : " is-invalid"}" data-box-tab="${index}" data-focus-key="box-tab-${index}"><strong>Box ${index + 1}</strong><small>${status}</small></button>`;
      }).join("");

      const prompt = this.root.querySelector("[data-selection-prompt]");
      if (prompt) {
        prompt.textContent = filledCount === SLOT_COUNT
          ? `Box ${this.state.activeIndex + 1} lineup complete. Choose any position to swap it.`
          : `Choose a flavour for position ${this.state.selectedSlot + 1}`;
      }
      const progressLabel = this.root.querySelector("[data-selection-progress-label]");
      if (progressLabel) progressLabel.textContent = `${filledCount} of ${SLOT_COUNT} selected`;
      const progressMeter = this.root.querySelector("[data-selection-meter]");
      progressMeter?.setAttribute("aria-valuenow", String(filledCount));
      progressMeter?.setAttribute("aria-valuetext", `${filledCount} of ${SLOT_COUNT} positions filled`);
      const progressBar = this.root.querySelector("[data-selection-progress]");
      if (progressBar) progressBar.style.width = `${filledCount / SLOT_COUNT * 100}%`;

      const slots = this.root.querySelector("[data-box-slots]");
      const flavourDialogId = this.root.querySelector("[data-flavour-dialog]")?.id || "";
      slots.innerHTML = box.slots.map((variantId, index) => {
        const currentFlavour = this.flavour(variantId);
        const selected = this.state.selectedSlot === index;
        const image = this.flavourImage(variantId);
        const title = currentFlavour?.name || "Choose a flavour";
        const actions = variantId
          ? `<div class="fl-gift-builder__slot-actions"><button type="button" data-move-slot="left" data-slot-index="${index}" data-focus-key="move-left-${index}" aria-label="Move position ${index + 1} left"${index === 0 ? " disabled" : ""}>←</button><button type="button" data-clear-slot="${index}" data-focus-key="clear-${index}" aria-label="Clear position ${index + 1}">Clear</button><button type="button" data-move-slot="right" data-slot-index="${index}" data-focus-key="move-right-${index}" aria-label="Move position ${index + 1} right"${index === SLOT_COUNT - 1 ? " disabled" : ""}>→</button></div>`
          : "";
        return `<article role="listitem" class="fl-gift-builder__slot${selected ? " is-selected" : ""}${variantId ? " is-filled" : ""}"><button type="button" class="fl-gift-builder__slot-choice" data-select-slot="${index}" data-focus-key="slot-${index}" aria-haspopup="dialog"${flavourDialogId ? ` aria-controls="${escapeHtml(flavourDialogId)}"` : ""} aria-label="Position ${index + 1}: ${escapeHtml(title)}. ${variantId ? "Choose a replacement" : "Choose a flavour"}"><span class="fl-gift-builder__slot-number"><small>Position</small>${index + 1}</span><span class="fl-gift-builder__slot-media">${image ? `<img src="${escapeHtml(image)}" alt="" loading="lazy">` : '<span class="fl-gift-builder__slot-empty">Choose</span>'}</span><strong>${escapeHtml(title)}</strong><small class="fl-gift-builder__slot-state">${variantId ? "Click to replace" : "Click to choose"}</small></button>${currentFlavour && !currentFlavour.available ? `<small class="fl-gift-builder__slot-warning">Choose another flavour; this one is sold out.</small>` : ""}${actions}</article>`;
      }).join("");

      const palette = this.root.querySelector("[data-flavour-palette]");
      if (palette) palette.innerHTML = this.config.flavours.map((flavour, index) => {
        const soldOut = !flavour.available;
        const usedCount = box.slots.filter((variantId) => variantId === flavour.variantId).length;
        const status = soldOut
          ? "Sold out"
          : usedCount ? `Used ${usedCount} ${usedCount === 1 ? "time" : "times"}` : `Pick for position ${this.state.selectedSlot + 1}`;
        const action = soldOut
          ? `${this.flavourLabel(flavour.variantId)}, sold out`
          : `${this.flavourLabel(flavour.variantId)}. Choose for position ${this.state.selectedSlot + 1}.${usedCount ? ` Already used ${usedCount} ${usedCount === 1 ? "time" : "times"}.` : ""}`;
        return `<div class="fl-gift-builder__flavour-tile" role="listitem"><button type="button" class="fl-gift-builder__flavour${soldOut ? " is-unavailable" : ""}${usedCount ? " is-used" : ""}" data-flavour-id="${escapeHtml(flavour.variantId)}" data-focus-key="flavour-${index}"${soldOut ? " disabled" : ""} aria-label="${escapeHtml(action)}"><span class="fl-gift-builder__flavour-media">${flavour.image ? `<img src="${escapeHtml(flavour.image)}" alt="" loading="lazy">` : ""}</span><span class="fl-gift-builder__flavour-name">${escapeHtml(this.flavourLabel(flavour.variantId))}</span><small>${escapeHtml(status)}</small></button></div>`;
      }).join("");

      const dialogTitle = this.root.querySelector("[data-flavour-dialog-title]");
      if (dialogTitle) dialogTitle.textContent = `Choose a flavour for position ${this.state.selectedSlot + 1}`;

      this.root.querySelectorAll("[data-brief-field]").forEach((field) => {
        field.value = box.brief[field.dataset.briefField] || "";
      });
      const message = this.root.querySelector("[data-gift-message]");
      message.value = box.message;
      this.updateMessageCount();

      const preferredDeliveryDate = this.root.querySelector("[data-preferred-delivery-date]");
      if (preferredDeliveryDate) {
        preferredDeliveryDate.min = todayIso();
        preferredDeliveryDate.value = this.state.preferredDeliveryDate || "";
        preferredDeliveryDate.setAttribute("aria-invalid", "false");
      }

      const suggestionsNode = this.root.querySelector("[data-message-suggestions]");
      suggestionsNode.hidden = !this.suggestions.length;
      suggestionsNode.innerHTML = this.suggestions.length
        ? `<p><strong>Choose a message:</strong></p>${this.suggestions.map((item, index) => `<button type="button" data-message-suggestion="${index}">${escapeHtml(item)}</button>`).join("")}`
        : "";

      const status = this.root.querySelector("[data-box-status]");
      const readyCount = this.state.boxes.filter((entry) => this.boxReady(entry)).length;
      status.textContent = readyCount === this.state.boxes.length
        ? `${readyCount} ${readyCount === 1 ? "box is" : "boxes are"} ready.`
        : `${readyCount} of ${this.state.boxes.length} boxes ready.`;

      const add = this.root.querySelector("[data-add-gift-boxes]");
      this.syncAddButtonState();
      add.setAttribute("aria-busy", String(this.adding));
      add.textContent = this.adding ? "Adding boxes…" : add.dataset.defaultLabel;

      const generate = this.root.querySelector("[data-generate-message]");
      generate.disabled = this.generating;
      generate.setAttribute("aria-busy", String(this.generating));
      generate.textContent = this.generating ? "Generating…" : "Generate message ideas";

      const summary = this.root.querySelector("[data-box-summary]");
      const requestedDeliveryDate = formatDeliveryDate(this.state.preferredDeliveryDate);
      const deliverySummary = this.root.querySelector("[data-delivery-summary]");
      deliverySummary.innerHTML = requestedDeliveryDate
        ? `<strong>Preferred delivery date:</strong> ${escapeHtml(requestedDeliveryDate)} <span>(request only; not guaranteed)</span>`
        : "<strong>Preferred delivery date:</strong> No date requested";
      const reviewPrice = this.config.studioEnabled && this.config.currency && Number.isFinite(this.config.unitPrice)
        ? new Intl.NumberFormat(document.documentElement.lang || "en-ZA", { style: "currency", currency: this.config.currency }).format((this.config.currency === "ZAR" ? globalThis.FlGiftStudio?.offerQuote?.(this.config.offers, this.state.boxes.length, this.studioCartCount || 0)?.unit ?? this.config.unitPrice : this.config.unitPrice) / 100)
        : "";
      summary.innerHTML = this.state.boxes.map((entry, index) => `<article><h4>Box ${index + 1}${entry.recipientLabel ? ` · ${escapeHtml(entry.recipientLabel)}` : ""}</h4>${reviewPrice ? `<p class="fl-gift-studio-box-price">${escapeHtml(reviewPrice)} / box</p>` : ""}<ol>${entry.slots.map((id) => `<li>${escapeHtml(this.flavourLabel(id))}</li>`).join("")}</ol><p><strong>Message:</strong> ${entry.message ? escapeHtml(entry.message) : "No message"}</p>${this.config.studioEnabled ? `<button type="button" data-box-tab="${index}" data-studio-edit-box>Edit Box ${index + 1}</button>` : ""}</article>`).join("");
      this.renderGuide();
      if (activeFocusKey) {
        const preferred = this.root.querySelector(`[data-focus-key="${activeFocusKey}"]`);
        const fallback = this.root.querySelector(this.mobileQuery?.matches
          ? `[data-guide-slot="${this.state.selectedSlot}"]`
          : `[data-focus-key="slot-${this.state.selectedSlot}"]`);
        const focusTarget = preferred && !preferred.disabled ? preferred : fallback;
        focusTarget?.focus?.({ preventScroll: true });
      }
    }
  }

  document.querySelectorAll("[data-fl-gift-builder]").forEach((root) => {
    new GiftBoxBuilder(root).start();
  });
})();
